import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { personelKarti } from '../api/organizasyon';
import {
  atamaYenidenHesapla,
  DUZELTME_GEREKCELERI,
  gunHareketleri,
  gunOzeti,
  personelPuantaji,
  puantajDuzelt,
  type AylikPuantajGunu,
  type DuzeltmeGerekcesi,
  type HareketKaydi,
} from '../api/puantaj';
import type { Kimlik } from '../api/tipler';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu, Metin } from '../bilesenler/temel';
import { saatMetni, tarihMetni, zamanMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * KİŞİ BAZLI PUANTAJ ÇİZELGESİ.
 *
 * Puantaj denetimi ekranı YALNIZ İŞARETLİ günleri gösteriyor — bakılması
 * gerekenleri. Bu ekran ise bir kişinin AYIN TAMAMINI gösterir: normal
 * günler dahil. İkisi ayrı çünkü sorular ayrı: denetim "neye bakmalıyım",
 * çizelge "bu kişi bu ay ne kazandı".
 *
 * AY BAZLI, serbest aralık değil. Mesai kotası ve dönem kapanışı ay üzerinden
 * işliyor; serbest aralıkta ekrandaki toplam ile dönem özeti tutmazdı.
 *
 * SÜRELER DAKİKA, TUTARLAR METİN. Tutar `Number()`a çevrilirse aylık
 * toplamda kuruş kaybı birikir (SÖZLEŞME §13'teki `tutarMetni` gerekçesi).
 */

const AYLAR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

const DURUM_BICIMI: Record<string, string> = {
  hesaplandi: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  beklemede: 'bg-slate-100 text-slate-600 ring-slate-200',
  kilitli: 'bg-slate-800 text-white ring-slate-800',
  itirazli: 'bg-amber-50 text-amber-800 ring-amber-200',
  duzeltildi: 'bg-sky-50 text-sky-700 ring-sky-200',
};

/** Dakikayı `7s 30d` yapar; 0 ise tire. */
function sure(dakika: number): string {
  if (dakika === 0) return '—';
  const s = Math.floor(Math.abs(dakika) / 60);
  const d = Math.abs(dakika) % 60;
  const im = dakika < 0 ? '-' : '';
  if (s === 0) return `${im}${d}d`;
  return d === 0 ? `${im}${s}s` : `${im}${s}s ${d}d`;
}

export function PuantajCizelgesi() {
  const { atamaId } = useParams<{ atamaId: string }>();
  const gezin = useNavigate();

  const bugun = new Date();
  const [yil, yilYaz] = useState(bugun.getFullYear());
  const [ay, ayYaz] = useState(bugun.getMonth() + 1);
  const [acikGun, acikGunYaz] = useState<AylikPuantajGunu | null>(null);

  const duzeltebilir = yetkiKullan('PUANTAJ_DUZELT');

  const cizelge = useQuery({
    queryKey: ['puantaj', 'cizelge', atamaId, yil, ay],
    queryFn: () => personelPuantaji(atamaId!, yil, ay),
    enabled: atamaId !== undefined,
  });

  const gunler = cizelge.data ?? [];

  /*
   * TOPLAMLAR İSTEMCİDE HESAPLANIYOR ve bu bilinçli: uç gün listesi
   * döndürüyor, ayrı bir toplam ucu yok. Tutarlar METİN olarak toplanamaz,
   * o yüzden burada YALNIZ DAKİKA toplanır — ücret toplamı için dönem özeti
   * ekranı kullanılır. Tutarı burada toplasaydık kuruş kaybı birikirdi.
   */
  const toplam = gunler.reduce(
    (t, g) => ({
      net: t.net + g.netDakika,
      normal: t.normal + g.normalDakika,
      fazlaSureli: t.fazlaSureli + g.fazlaSureliDakika,
      fazlaCalisma: t.fazlaCalisma + g.fazlaCalismaDakika,
      haftaTatili: t.haftaTatili + g.haftaTatiliDakika,
      gece: t.gece + g.geceDakika,
      devamsiz: t.devamsiz + (g.devamsizMi ? 1 : 0),
    }),
    { net: 0, normal: 0, fazlaSureli: 0, fazlaCalisma: 0, haftaTatili: 0, gece: 0, devamsiz: 0 },
  );

  const kisi = useQuery({
    queryKey: ['puantaj', 'cizelge-kisi', atamaId],
    queryFn: () => personelKarti(atamaId!),
    enabled: atamaId !== undefined,
    // Atama kimliği personel kimliği DEĞİL; kart bulunamazsa başlık sade kalır.
    retry: false,
  });

  const sutunlar: Sutun<AylikPuantajGunu>[] = [
    {
      baslik: 'Gün',
      hucre: (g) => (
        <span className="whitespace-nowrap">
          <span className="block font-medium text-slate-900">{tarihMetni(g.tarih)}</span>
          {g.devamsizMi && <span className="block text-xs text-red-600">devamsız</span>}
        </span>
      ),
      daralt: true,
    },
    {
      baslik: 'Giriş – Çıkış',
      hucre: (g) => (
        <span className="whitespace-nowrap text-xs">
          {g.ilkGiris === null ? (
            <span className="text-slate-400">basma yok</span>
          ) : (
            `${saatMetni(g.ilkGiris)} – ${saatMetni(g.sonCikis)}`
          )}
        </span>
      ),
      daralt: true,
    },
    { baslik: 'Net', hucre: (g) => sure(g.netDakika), daralt: true, hizala: 'sag' },
    { baslik: 'Normal', hucre: (g) => sure(g.normalDakika), daralt: true, hizala: 'sag' },
    {
      baslik: 'Fazla süreli',
      // ×1,25 — 45 saate kadar olan kısım (4857/41).
      hucre: (g) => sure(g.fazlaSureliDakika),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Fazla çalışma',
      // ×1,50 — 45 saati aşan kısım.
      hucre: (g) => (
        <span className={g.fazlaCalismaDakika > 0 ? 'font-medium text-amber-800' : ''}>
          {sure(g.fazlaCalismaDakika)}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Hafta tatili',
      // ×1,50 + asgari 1 gün tabanı.
      hucre: (g) => sure(g.haftaTatiliDakika),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Gün bazlı',
      /*
       * RESMİ TATİL, DİNİ BAYRAM VE YARIM GÜN SAAT DEĞİL GÜN olarak
       * ücretlendirilir; saat sütunlarına karıştırılmaz. Değerler METİN
       * (`0.50` gibi) ve öyle basılır.
       */
      hucre: (g) => {
        const parcalar = [
          g.resmiTatilGun !== '0.00' ? `resmi ${g.resmiTatilGun}` : null,
          g.diniBayramGun !== '0.00' ? `dini ${g.diniBayramGun}` : null,
          g.yarimGunTatilGun !== '0.00' ? `yarım ${g.yarimGunTatilGun}` : null,
        ].filter((x) => x !== null);
        return parcalar.length === 0 ? '—' : parcalar.join(' · ');
      },
      daralt: true,
    },
    {
      baslik: 'Durum',
      hucre: (g) => (
        <span className="flex items-center gap-1">
          <span
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
              DURUM_BICIMI[g.durum] ?? DURUM_BICIMI.beklemede
            }`}
          >
            {g.durum}
          </span>
          {g.isaretKodlari.length > 0 && (
            <span
              title={g.isaretKodlari.join(' · ')}
              className="rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-800"
            >
              {g.isaretKodlari.length}
            </span>
          )}
        </span>
      ),
      daralt: true,
    },
    {
      baslik: '',
      hucre: (g) => (
        <button
          type="button"
          onClick={() => acikGunYaz(g)}
          className="whitespace-nowrap rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
        >
          Ayrıntı
        </button>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            // `navigate` Promise döndürüyor (react-router 7); `void` ile
            // açıkça yok sayılır, aksi halde işlenmemiş reddedilme uyarısı çıkar.
            onClick={() => void gezin(-1)}
            className="text-sm text-kurum-700 hover:underline"
          >
            ← Geri
          </button>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">
            {kisi.data ? `${kisi.data.ad} ${kisi.data.soyad}` : 'Puantaj çizelgesi'}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {kisi.data ? `${kisi.data.personelNo} · ${kisi.data.birimAd}` : `Atama ${atamaId}`}
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-slate-700">
            <span className="mb-1 block font-medium">Ay</span>
            <select
              value={ay}
              onChange={(olay) => ayYaz(Number(olay.target.value))}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              {AYLAR.map((a, i) => (
                <option key={a} value={i + 1}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <div className="w-28">
            <Metin etiket="Yıl" deger={String(yil)} degisti={(y) => yilYaz(Number(y) || yil)} />
          </div>
        </div>
      </header>

      {/*
        AY TOPLAMLARI ÜSTTE. Tabloyu sonuna kadar kaydırmadan "bu ay ne oldu"
        sorusunun cevabı görünmeli; puantaj tartışması bu dört sayı üzerinden
        yürüyor.
      */}
      <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Sayac etiket="Net" deger={sure(toplam.net)} />
        <Sayac etiket="Normal" deger={sure(toplam.normal)} />
        <Sayac etiket="Fazla süreli ×1,25" deger={sure(toplam.fazlaSureli)} />
        <Sayac etiket="Fazla çalışma ×1,50" deger={sure(toplam.fazlaCalisma)} vurgula />
        <Sayac etiket="Hafta tatili ×1,50" deger={sure(toplam.haftaTatili)} />
        <Sayac
          etiket="Devamsız"
          deger={toplam.devamsiz === 0 ? '—' : `${toplam.devamsiz} gün`}
          vurgula={toplam.devamsiz > 0}
        />
      </dl>

      <HataKutusu hata={cizelge.error} />

      <Tablo
        sutunlar={sutunlar}
        satirlar={gunler}
        anahtar={(g) => g.tarih}
        bekliyor={cizelge.isPending}
        bosMetin="Bu ayda puantaj kaydı yok."
        satirBicimi={(g) => (g.devamsizMi ? 'bg-red-50/40' : undefined)}
      />

      {acikGun !== null && atamaId !== undefined && (
        <GunAyrintisi
          atamaId={atamaId}
          gun={acikGun}
          duzeltebilir={duzeltebilir}
          kapat={() => acikGunYaz(null)}
        />
      )}
    </div>
  );
}

function Sayac({
  etiket,
  deger,
  vurgula = false,
}: {
  etiket: string;
  deger: string;
  vurgula?: boolean;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd
        className={`mt-0.5 text-lg font-semibold ${vurgula ? 'text-amber-800' : 'text-slate-900'}`}
      >
        {deger}
      </dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gün ayrıntısı                                                       */
/* ------------------------------------------------------------------ */

/**
 * GÜN AYRINTISI — üç soruyu birlikte cevaplar:
 *   1. Ne basıldı        → hareket listesi (kim bastı, hangi kaynaktan)
 *   2. Nasıl hesaplandı  → kalem kalem mesai tipi, dakika, katsayı, tutar
 *   3. Neden işaretli    → işaret kodları
 *
 * Üçü ayrı ekranda olsaydı bir itirazı incelemek için üç yere bakmak
 * gerekirdi; itiraz penceresi beş iş günü ve şef bu ekranda karar veriyor.
 */
function GunAyrintisi({
  atamaId,
  gun,
  duzeltebilir,
  kapat,
}: {
  atamaId: Kimlik;
  gun: AylikPuantajGunu;
  duzeltebilir: boolean;
  kapat: () => void;
}) {
  const sorguIstemcisi = useQueryClient();
  const tarih = gun.tarih.slice(0, 10);

  const [duzeltilen, duzeltilenYaz] = useState<HareketKaydi | null>(null);
  const [hesapAcik, hesapAcikYaz] = useState(false);

  const ozet = useQuery({
    queryKey: ['puantaj', 'gun-ozeti', atamaId, tarih],
    queryFn: () => gunOzeti(atamaId, tarih),
  });

  const hareketler = useQuery({
    queryKey: ['puantaj', 'gun-hareket', atamaId, tarih],
    queryFn: () => gunHareketleri(atamaId, tarih),
  });

  const hesapla = useMutation({
    mutationFn: (gerekce: string) => atamaYenidenHesapla(atamaId, tarih, gerekce),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['puantaj'] });
      hesapAcikYaz(false);
    },
  });

  return (
    <Diyalog
      baslik={`${tarihMetni(gun.tarih)} — gün ayrıntısı`}
      aciklama={`Net ${sure(gun.netDakika)} · durum ${gun.durum}`}
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-5">
          {gun.isaretKodlari.length > 0 && (
            <section className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                İşaretler
              </h3>
              <p className="mt-1 text-sm text-slate-800">{gun.isaretKodlari.join(' · ')}</p>
            </section>
          )}

          {/* --- 1) Basmalar --- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-800">Basmalar</h3>
            <HataKutusu hata={hareketler.error} />

            {hareketler.isPending ? (
              <p className="mt-1 text-sm text-slate-500">Yükleniyor…</p>
            ) : (hareketler.data?.length ?? 0) === 0 ? (
              <p className="mt-1 text-sm text-slate-500">Bu güne ait basma yok.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {hareketler.data?.map((h) => (
                  <li
                    key={h.hareketId}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="text-sm">
                      <span className="font-medium text-slate-900">{h.hareketTipi}</span>
                      <span className="ml-2 text-xs text-slate-500">{zamanMetni(h.zaman)}</span>
                    </span>
                    <span className="flex items-center gap-2 text-xs">
                      {/*
                        KAYNAK GÖSTERİLİR: kişinin kendi basması ile puantörün
                        onun adına basması ayırt edilmeli — itirazın çıkış
                        noktası genelde budur.
                      */}
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">
                        {h.kaynak}
                      </span>
                      {h.basanAtamaId !== null && h.basanAtamaId !== String(atamaId) && (
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-sky-800">
                          başkası bastı
                        </span>
                      )}
                      {duzeltebilir && (
                        <button
                          type="button"
                          onClick={() => duzeltilenYaz(h)}
                          className="rounded px-2 py-1 text-kurum-700 hover:bg-kurum-50"
                        >
                          Düzelt
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {ozet.data && (
              <p className="mt-2 text-xs text-slate-500">
                {ozet.data.hareketSayisi} hareket
                {ozet.data.puantorBasimiVar && ' · puantör basımı var'}
                {ozet.data.otomatikKapanmaVar && ' · otomatik kapanma'}
                {ozet.data.duzeltmeVar && ' · düzeltilmiş'}
              </p>
            )}
          </section>

          {/* --- 2) Hesap kalemleri --- */}
          <section>
            <h3 className="text-sm font-semibold text-slate-800">Hesap kalemleri</h3>
            <p className="text-xs text-slate-500">
              Hangi mesai tipi, kaç dakika, hangi yöntemle ücretlendirildi.
            </p>

            {gun.kalemler.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">Ücrete esas kalem yok.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded-md border border-slate-200">
                {gun.kalemler.map((k, i) => (
                  <li
                    key={`${k.mesaiTipi}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span>
                      <span className="block font-medium text-slate-900">{k.mesaiTipi}</span>
                      {k.aciklama !== null && (
                        <span className="block text-xs text-slate-500">{k.aciklama}</span>
                      )}
                    </span>
                    <span className="whitespace-nowrap text-xs text-slate-600">
                      {/* Tutar METİN olarak basılır — çevrilirse kuruş kaybı olur. */}
                      {k.dakika > 0 ? sure(k.dakika) : `${k.gun} gün`} ·{' '}
                      <span className="text-slate-500">{k.yontem}</span> ·{' '}
                      <span className="font-medium text-slate-900">{k.tutar}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <HataKutusu hata={hesapla.error} />

          <div className="flex flex-wrap justify-end gap-2">
            {duzeltebilir && (
              <Dugme tur="ikincil" tikla={() => hesapAcikYaz(true)} cocuk="Günü yeniden hesapla" />
            )}
            <Dugme tur="ikincil" tikla={kapat} cocuk="Kapat" />
          </div>

          {hesapAcik && (
            <GerekceDiyalogu
              baslik="Günü yeniden hesapla"
              aciklama={`${tarihMetni(gun.tarih)} — mevcut hesap silinip yeniden üretilir.`}
              etiket="Gerekçe"
              ipucu="Zorunlu — toplu hesap da bir müdahaledir, denetim izine yazılır."
              onayEtiketi="Hesapla"
              enCokKarakter={500}
              bekliyor={hesapla.isPending}
              hata={hesapla.error}
              kapat={() => hesapAcikYaz(false)}
              onayla={(gerekce) => hesapla.mutate(gerekce)}
            />
          )}

          {duzeltilen !== null && (
            <DuzeltmeDiyalogu
              atamaId={atamaId}
              hareket={duzeltilen}
              kapat={() => duzeltilenYaz(null)}
            />
          )}
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Düzeltme                                                            */
/* ------------------------------------------------------------------ */

/**
 * ŞEF DÜZELTMESİ.
 *
 * DÜZELTİLEN KAYIT SİLİNMEZ: `duzeltilenId` ve `duzeltilenZaman` sunucuya
 * gönderilir, eski değer denetim izinde kalır ve yeni kayıt onun üzerine
 * EKLENİR. "Şef saati değiştirdi mi" sorusu her zaman cevaplanabilir olmalı.
 *
 * `duzeltilenZaman` de gerekiyor çünkü `devam_hareketi` partition'lı —
 * yalnız kimlikle satır bulunamaz, partition anahtarı şart.
 *
 * GEREKÇE KAPALI LİSTE. Serbest metin olsaydı düzeltme sebepleri sayılamaz
 * ve pilotun "veri kalitesi" ölçümü yapılamazdı.
 */
function DuzeltmeDiyalogu({
  atamaId,
  hareket,
  kapat,
}: {
  atamaId: Kimlik;
  hareket: HareketKaydi;
  kapat: () => void;
}) {
  const sorguIstemcisi = useQueryClient();

  // Mevcut zamanı `HH:MM` olarak ön doldur: şef genelde saati düzeltiyor,
  // tarihi değil.
  const [saat, saatYaz] = useState(() => saatMetni(hareket.zaman));

  const duzelt = useMutation({
    mutationFn: (girdi: { gerekce: DuzeltmeGerekcesi; not: string }) => {
      const gun = hareket.zaman.slice(0, 10);
      return puantajDuzelt({
        atamaId,
        duzeltilenId: hareket.hareketId,
        duzeltilenZaman: hareket.zaman,
        zaman: `${gun}T${saat}:00+03:00`,
        gerekce: girdi.gerekce,
        not: girdi.not === '' ? undefined : girdi.not,
      });
    },
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['puantaj'] });
      kapat();
    },
  });

  return (
    <GerekceDiyalogu
      baslik="Basma kaydını düzelt"
      aciklama={`${hareket.hareketTipi} · mevcut ${zamanMetni(hareket.zaman)} — eski kayıt SİLİNMEZ, düzeltme üzerine eklenir.`}
      etiket="Not (isteğe bağlı)"
      ipucu="Sebep aşağıdan seçilir; not serbest açıklamadır."
      onayEtiketi="Düzelt"
      zorunluMu={false}
      enCokKarakter={500}
      secenekEtiketi="Düzeltme sebebi"
      secenekler={DUZELTME_GEREKCELERI.map((g) => ({ kod: g.kod, etiket: g.etiket }))}
      bekliyor={duzelt.isPending}
      hata={duzelt.error}
      kapat={kapat}
      onayla={(not, secenekKodu) =>
        duzelt.mutate({ gerekce: (secenekKodu ?? 'diger') as DuzeltmeGerekcesi, not })
      }
      ustBilgi={
        <Metin
          etiket="Yeni saat (HH:MM)"
          tur="time"
          deger={saat}
          degisti={saatYaz}
          ipucu="Yalnız saat değişir; gün aynı kalır."
        />
      }
    />
  );
}
